import { html, css, LitElement } from 'lit-element';
import { db } from './intrivity_app.js';
import { doc, getDoc, setDoc } from 'firebase/firestore';

import { hasRole } from './utility.js';
import { t, Localized, LOCALES } from './i18n.js';
import { EMPTY_TEMPLATE, templateLanguages, getTemplateLanguage, buildTemplate } from './invitation_templates.js';

import '@material/mwc-button';
import '@material/mwc-textfield';
import '@material/mwc-textarea';

// SuperAdmin-only editor for the system-wide default invitation email --
// Firestore doc templates/invitation, the last fallback in
// resolveInviteTemplate() (functions/invitation_language.js) when neither a group nor its
// client has its own invitation_template override (see
// intrivity-invitation-template-dialog.js for that per-client/group editor,
// whose rich-text editing pattern this reuses). Unlike that dialog, this is
// an always-visible inline form -- there's only one such document, not one
// per row -- and it writes with setDoc rather than updateDoc since the doc
// may not exist yet.
export class IntrivityAdminInvitationTemplate extends Localized(LitElement) {
  static get properties() {
    return {
      user_profile: { type: Object },
      _formData: { type: Object, state: true },
      _isLoading: { type: Boolean, state: true },
      _isSaving: { type: Boolean, state: true },
      _htmlSourceMode: { type: Boolean, state: true },
      _lang: { type: String, state: true },         // language being edited
    };
  }

  constructor() {
    super();
    this._formData = { ...EMPTY_TEMPLATE };
    this._isLoading = true;
    this._isSaving = false;
    this._htmlSourceMode = false;
    // Every language's copy while editing; _formData is the one on screen.
    this._lang = 'en';
    this._byLanguage = {};
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    this._isLoading = true;
    try {
      const snap = await getDoc(doc(db, 'templates', 'invitation'));
      const data = snap.exists() ? snap.data() : null;
      this._byLanguage = {};
      for (const lang of templateLanguages()) {
        this._byLanguage[lang] = getTemplateLanguage(data, lang) || { ...EMPTY_TEMPLATE };
      }
      this._formData = { ...this._byLanguage[this._lang] };
    } catch (e) {
      console.error('Could not load the default invitation template:', e);
      alert(t('defaultTemplate.loadError', { message: e.message || e }));
    } finally {
      this._isLoading = false;
      await this._syncEditableFromFormData();
    }
  }

  // The rich-text area's content lives in the DOM, not a Lit template
  // binding (see render()) -- rebinding .innerHTML reactively on every
  // keystroke would reset the cursor/selection and undo history. So it's
  // only ever pushed in imperatively: on load, and when switching back from
  // source mode.
  async _syncEditableFromFormData() {
    await this.updateComplete;
    const el = this.shadowRoot.getElementById('html-editable');
    if (el) el.innerHTML = this._formData.html || '';
  }

  _handleInput(e) {
    const { name, value } = e.target;
    this._formData = { ...this._formData, [name]: value };
  }

  _handleEditableInput(e) {
    this._formData = { ...this._formData, html: e.currentTarget.innerHTML };
  }

  _execCommand(command, value = null) {
    const el = this.shadowRoot.getElementById('html-editable');
    if (!el) return;
    el.focus();
    if (command === 'createLink') {
      const url = prompt(t('editor.linkPrompt'), 'https://');
      if (!url) return;
      value = url;
    }
    document.execCommand(command, false, value);
    this._formData = { ...this._formData, html: el.innerHTML };
  }

  async _toggleHtmlSourceMode() {
    this._htmlSourceMode = !this._htmlSourceMode;
    if (!this._htmlSourceMode) {
      // Coming back from source mode -- push whatever was typed there into
      // the rich-text view.
      await this._syncEditableFromFormData();
    }
  }

  async _switchLanguage(lang) {
    this._byLanguage[this._lang] = { ...this._formData };
    this._lang = lang;
    this._formData = { ...(this._byLanguage[lang] || EMPTY_TEMPLATE) };
    await this._syncEditableFromFormData();
  }

  async _save() {
    if (!hasRole('superadmin', this.user_profile)) return;
    this._isSaving = true;
    try {
      this._byLanguage[this._lang] = { ...this._formData };
      await setDoc(doc(db, 'templates', 'invitation'), buildTemplate(this._byLanguage));
      alert(t('defaultTemplate.saved'));
    } catch (e) {
      console.error('Error saving the default invitation template:', e);
      alert(t('defaultTemplate.saveError', { message: e.message || e }));
    } finally {
      this._isSaving = false;
    }
  }

  static get styles() {
    return css`
      mwc-textfield, mwc-textarea {
        width: 100%;
        margin-top: 8px;
      }
      .language-row {
        display: block;
        margin-top: 8px;
      }
      .hint {
        font-size: 0.85em;
        color: #666;
        margin-top: 0;
      }
      .html-field-label {
        font-size: 0.75em;
        color: #666;
        margin-top: 8px;
      }
      .html-toolbar {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 4px;
        padding: 4px;
        background: #f5f5f5;
        border: 1px solid #ccc;
        border-bottom: none;
      }
      .html-toolbar button {
        min-width: 28px;
        padding: 4px 8px;
        border: 1px solid #ccc;
        background: white;
        border-radius: 3px;
        cursor: pointer;
        font-size: 0.85em;
      }
      .html-toolbar button:hover {
        background: #eee;
      }
      .html-toolbar .spacer {
        flex: 1 1 auto;
      }
      #html-editable {
        width: 100%;
        min-height: 200px;
        max-height: 400px;
        overflow-y: auto;
        box-sizing: border-box;
        padding: 12px;
        border: 1px solid #ccc;
        font-family: inherit;
        font-size: 1em;
      }
      #html-editable:focus {
        outline: 2px solid #6200ee;
        outline-offset: -1px;
      }
      .html-source {
        width: 100%;
        box-sizing: border-box;
        font-family: monospace;
        font-size: 0.85em;
      }
    `;
  }

  render() {
    if (!hasRole('superadmin', this.user_profile)) return html``;
    return html`
      <div style="margin: 10px; max-width: 720px;">
        <h3>${t('defaultTemplate.title')}</h3>
        <p class="hint">
          ${t('defaultTemplate.hint', { button: t('groups.editInvitation') })}
        </p>
        <p class="hint">
          ${t('editor.placeholders')} [[client_name]], [[client_institution]], [[client_address]], [[client_email]],
          [[group_name]], [[group_description]], [[email]], [[code]], [[first]], [[last]], [[link]].
        </p>
        ${this._isLoading ? html`<p>${t('common.loading')}</p>` : html`
          <label class="language-row">
            ${t('invitationLanguage.editing')}
            <select @change=${(e) => this._switchLanguage(e.target.value)}>
              ${templateLanguages().map((code) => html`
                <option value=${code} ?selected=${code === this._lang}>${LOCALES[code].name}</option>
              `)}
            </select>
          </label>
          <p class="hint">${t('invitationLanguage.hint')}</p>
          <mwc-textfield
            label=${t('editor.subject')}
            name="subject"
            .value=${this._formData.subject}
            @input=${this._handleInput}
            ?disabled=${this._isSaving}>
          </mwc-textfield>
          <div class="html-field-label" id="html-field-label">${t('editor.htmlTemplate')}</div>
          <div class="html-toolbar" role="toolbar" aria-label=${t('editor.toolbar')}>
            <button type="button" title=${t('editor.bold')} aria-label=${t('editor.bold')} style="font-weight:bold;" @click=${() => this._execCommand('bold')}>B</button>
            <button type="button" title=${t('editor.italic')} aria-label=${t('editor.italic')} style="font-style:italic;" @click=${() => this._execCommand('italic')}>I</button>
            <button type="button" title=${t('editor.underline')} aria-label=${t('editor.underline')} style="text-decoration:underline;" @click=${() => this._execCommand('underline')}>U</button>
            <button type="button" title=${t('editor.bulleted')} aria-label=${t('editor.bulleted')} @click=${() => this._execCommand('insertUnorderedList')}>• ${t('editor.list')}</button>
            <button type="button" title=${t('editor.numbered')} aria-label=${t('editor.numbered')} @click=${() => this._execCommand('insertOrderedList')}>1. ${t('editor.list')}</button>
            <button type="button" title=${t('editor.link')} aria-label=${t('editor.insertLink')} @click=${() => this._execCommand('createLink')}>${t('editor.link')}</button>
            <button type="button" title=${t('editor.removeLink')} aria-label=${t('editor.removeLink')} @click=${() => this._execCommand('unlink')}>${t('editor.unlink')}</button>
            <button type="button" title=${t('editor.clearFormatting')} aria-label=${t('editor.clearFormatting')} @click=${() => this._execCommand('removeFormat')}>${t('editor.clear')}</button>
            <span class="spacer"></span>
            <button type="button" @click=${this._toggleHtmlSourceMode}>${this._htmlSourceMode ? t('editor.visual') : t('editor.source')}</button>
          </div>
          ${this._htmlSourceMode ? html`
            <mwc-textarea
              class="html-source"
              rows="10"
              name="html"
              .value=${this._formData.html}
              @input=${this._handleInput}
              ?disabled=${this._isSaving}>
            </mwc-textarea>
          ` : html`
            <div
              id="html-editable"
              contenteditable
              role="textbox"
              aria-multiline="true"
              aria-labelledby="html-field-label"
              @input=${this._handleEditableInput}>
            </div>
          `}
          <mwc-textarea
            rows="8"
            label=${t('editor.textTemplate')}
            name="text"
            .value=${this._formData.text}
            @input=${this._handleInput}
            ?disabled=${this._isSaving}>
          </mwc-textarea>
          <mwc-button raised label="${this._isSaving ? t('common.saving') : t('common.save')}" @click=${this._save} ?disabled=${this._isSaving}></mwc-button>
        `}
      </div>
    `;
  }
}
if (!customElements.get('intrivity-admin-invitation-template')) customElements.define('intrivity-admin-invitation-template', IntrivityAdminInvitationTemplate);
